import React, { forwardRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import MaterialTable from 'material-table';
import ArrowDownward from '@material-ui/icons/ArrowDownward';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Search from '@material-ui/icons/Search';
import { createTheme, MuiThemeProvider, Paper, TablePagination } from '@material-ui/core';
import { exportToExcel } from '../../helpers/file';
import ToolBar, { useToolbar } from './ToolBar';
import ColumnsPopover from './ColumnsPopover';
import NotDataLogo from '../../assets/svg/no-data.svg';

const tableIcons = {
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
};

const theme = createTheme({
  palette: {
    primary: {
      main: '#6200EE',
    },
    secondary: {
      main: '#6200EE',
    },
  },
});

const Table = ({
  columns = [],
  data = [],
  title = '',
  pageSize = 0,
  pageNumber = 0,
  paging = false,
  searchable = true,
  download = true,
  onRowClick,
  onNextPage = () => {},
  count = 0,
  filters = false,
  showFirstLastPageButtons = false,
  defaultSelectedColumns = [],
  handleRefresh,
  actions = [],
  selection = false,
  fetching = false,
  actionsColumnIndex = -1,
  searchPlaceHolder,
  onSearch = () => {},
  initialSearchText = '',
  headerColor = '#01579b',
  textColor = '#FFF',
  maxBodyHeight = '65vh',
  style,
  actionButtons = [],
  showPredefinedViewsButton = false,
  currentView,
  onViewChange,
  selectedColumns,
  onSelectedColumnsChange,
}) => {
  function formatDateForExport(value) {
    if (value === null || value === undefined || value === '') return '';

    if (typeof value === 'number') {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('fr-FR');
      }
      return value;
    }

    if (typeof value === 'string' && /^\d{12,}$/.test(value)) {
      const date = new Date(Number(value));
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('fr-FR');
      }
    }

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString('fr-FR');
    }

    return value;
  }

  function handleDownload() {
    const exportColumns = safeSelectedElement.length > 0 ? safeSelectedElement : columns;
    const exportedData = data.map((row) => {
      const newRow = {};

      exportColumns.forEach((col) => {
        if (!col.field) return;
        const field = col.field.toLowerCase();
        const colTitle = (col.title || '').toLowerCase();
        const value = row[col.field];

        if (field.includes('date') || colTitle.includes('date')) {
          newRow[col.field] = formatDateForExport(value);
        } else {
          newRow[col.field] = value;
        }
      });

      return newRow;
    });

    exportToExcel(exportColumns, exportedData, 'export');
  }

  const { selectedElement, handleSelectedElement } = useToolbar(data, defaultSelectedColumns);

  const safeSelectedElement = Array.isArray(selectedColumns) ? selectedColumns : Array.isArray(selectedElement) ? selectedElement : [];

  const handleColumnsChange = (value) => {
    let newColumns = value;

    if (value?.target?.value) {
      newColumns = value.target.value;
    }

    if (!Array.isArray(newColumns)) {
      newColumns = [];
    }

    handleSelectedElement(newColumns);

    if (onSelectedColumnsChange) {
      onSelectedColumnsChange(newColumns);
    }
  };

  const [columnsMenuPosition, setColumnsMenuPosition] = useState(null);
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);

  const handleOpenColumnsMenu = (position) => {
    setColumnsMenuPosition((prev) => {
      if (columnsMenuOpen && prev) {
        return prev;
      }
      return position;
    });
    setColumnsMenuOpen(true);
  };

  const handleCloseColumnsMenu = () => {
    setColumnsMenuOpen(false);
  };

  const tableRef = useRef();

  const paginationPageSize = pageSize || (Array.isArray(data) && data.length > 0 ? data.length : 1);
  const displayPageSize = Array.isArray(data) && data.length > 0 ? Math.min(paginationPageSize, data.length) : paginationPageSize;
  const maxPage = count > 0 ? Math.max(Math.ceil(count / paginationPageSize) - 1, 0) : 0;
  const currentPageNumber = Math.min(pageNumber || 0, maxPage);
  const tableKey = `${displayPageSize}-${count ?? 0}-${Array.isArray(data) ? data.length : 0}`;
  const { height: styleHeight, ...paperStyle } = style || {};

  useEffect(() => {
    if (paging && (pageNumber || 0) > currentPageNumber) {
      onNextPage(currentPageNumber);
    }
  }, [paging, pageNumber, currentPageNumber, onNextPage]);

  return (
    <Paper
      style={{
        padding: '30px',
        height: 'fit-content',
        maxHeight: styleHeight || undefined,
        width: '850px',
        ...paperStyle,
      }}
      id={'table'}
    >
      <MuiThemeProvider theme={theme}>
        <MaterialTable
          key={tableKey}
          tableRef={tableRef}
          icons={tableIcons}
          columns={safeSelectedElement.map((c) => ({ ...c, tableData: undefined }))}
          data={data}
          title={title}
          options={{
            search: false,
            actionsColumnIndex: actionsColumnIndex,
            sorting: true,
            paging: paging,
            pageSizeOptions: [],
            paginationType: 'normal',
            initialPage: 0,
            pageSize: displayPageSize,
            showFirstLastPageButtons: showFirstLastPageButtons,
            loadingType: 'linear',
            maxBodyHeight: maxBodyHeight,
            headerStyle: {
              position: 'sticky',
              top: '0',
              backgroundColor: headerColor,
              color: textColor,
              textAlign: 'center',
              fontWeight: '600',
              lineHeight: '15px',
              fontFamily: 'canalfont-bold',
            },
            cellStyle: {
              lineHeight: '15px',
              textAlign: 'center',
              fontSize: 13,
            },
            selection: selection,
            showTextRowsSelected: false,
            tableLayout: 'auto',
          }}
          localization={{
            body: {
              emptyDataSourceMessage: (
                <div
                  style={{
                    position: 'absolute',
                    top: '20%',
                    width: '90%',
                  }}
                >
                  <img height={'50%'} width={'50%'} src={NotDataLogo} alt="no data Logo" />
                </div>
              ),
            },
          }}
          isLoading={fetching}
          onRowClick={onRowClick}
          actions={actions}
          components={{
            Toolbar: (props) => {
              const { selectedRows, actions: toolbarActions } = props;

              return (
                <ToolBar
                  handleRefresh={handleRefresh}
                  download={download}
                  handleDownload={handleDownload}
                  selection={safeSelectedElement}
                  handleSelectChange={handleColumnsChange}
                  searchable={searchable}
                  columns={columns}
                  searchPlaceHolder={searchPlaceHolder}
                  onSearch={onSearch}
                  initialText={initialSearchText}
                  filters={filters}
                  actions={toolbarActions}
                  selectedData={selectedRows}
                  actionButtons={actionButtons}
                  showPredefinedViewsButton={showPredefinedViewsButton}
                  currentView={currentView}
                  onViewChange={onViewChange}
                  onOpenColumnsMenu={handleOpenColumnsMenu}
                />
              );
            },
            Container: (props) => <Paper {...props} elevation={0} />,
            Pagination: (props) => (
              <TablePagination
                {...props}
                page={currentPageNumber}
                rowsPerPage={paginationPageSize}
                count={count ?? 0}
                onPageChange={(event, page) => {
                  onNextPage(page);
                }}
                onChangePage={(event, page) => {
                  onNextPage(page);
                }}
                onChangeRowsPerPage={(event) => {
                  if (props.onChangeRowsPerPage) {
                    props.onChangeRowsPerPage(event);
                  }
                }}
              />
            ),
          }}
        />
        <ColumnsPopover
          open={columnsMenuOpen}
          anchorPosition={columnsMenuPosition}
          onClose={handleCloseColumnsMenu}
          value={safeSelectedElement}
          options={columns}
          onChange={handleColumnsChange}
          iconColor="#6200EE"
        />
      </MuiThemeProvider>
    </Paper>
  );
};

Table.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  title: PropTypes.string,
  pageSize: PropTypes.number,
  pageNumber: PropTypes.number,
  paging: PropTypes.bool,
  searchable: PropTypes.bool,
  onRowClick: PropTypes.func,
  onNextPage: PropTypes.func,
  count: PropTypes.number,
  onSearch: PropTypes.func,
  initialSearchText: PropTypes.string,
  selectedColumns: PropTypes.array,
  onSelectedColumnsChange: PropTypes.func,
};

export default Table;
